import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import AccordionSection from '../../components/common/AccordionSection';
import Swal from 'sweetalert2';
import { getCessionInformation, setCessionStatus } from '../../services/cesionesService';
import { getUserSession } from '../../utils/auth';

// Obtiene la URL del documento para visualizar en iframe
const buildDocUrl = (doc) => {
  if (!doc) return null;
  if (doc.documentPath) return doc.documentPath;
  if (doc.documentUrl) return doc.documentUrl;
  if (!doc.documentB64) return null;
  const lower = (doc.documentName || '').toLowerCase();
  const mime = lower.endsWith('.pdf') ? 'application/pdf'
    : lower.endsWith('.png') ? 'image/png'
    : lower.endsWith('.jpg') || lower.endsWith('.jpeg') ? 'image/jpeg'
    : 'application/octet-stream';
  return `data:${mime};base64,${doc.documentB64}`;
};

const Fila = ({ label, value, highlight }) => (
  <div className="flex justify-between py-1 border-b border-gray-100 last:border-b-0">
    <span className="text-xs text-gray-500 flex-shrink-0 mr-2">{label}</span>
    <span className={`text-xs font-medium text-right ${highlight ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
      {value || 'N/A'}
    </span>
  </div>
);

const AprobacionCesion = ({ user, onLogout, onNavigate }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [cesion, setCesion] = useState(null);
  const [error, setError] = useState(null);
  const [docActual, setDocActual] = useState(0);
  const [modalAprobar, setModalAprobar] = useState(false);
  const [modalRechazar, setModalRechazar] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    const navData = location.state;
    if (!navData || (!navData.cessionID && !navData.grupo && !navData.cessionData)) {
      setError('No se recibieron datos de la cesion. Vuelva al listado y seleccione una cesion.');
      setLoading(false);
      return;
    }

    const mapData = (data) => ({
      cessionID: data.cessionID,
      grupo: data.objContract.group,
      orden: data.objContract.order,
      tipoFormulario: data.objContract.formType,
      personType: data.personType || data.objBuyer?.personType || '',
      estado: data.cessionStatus || navData.estado || '',
      statusNotes: data.statusNotes || '',
      comprador: {
        nombre: data.objBuyer?.fullName,
        dni: data.objBuyer?.ident,
        telefono: data.objBuyer?.phone,
        email: data.objBuyer?.email,
        tipo: data.objBuyer?.personType,
      },
      vendedor: {
        nombre: data.objSeller?.fullName,
        dni: data.objSeller?.ident,
        telefono: data.objSeller?.phone,
        email: data.objSeller?.email,
        tipo: data.objSeller?.personType,
      },
      documentos: data.documents || [],
    });

    // Si ya viene la información pre-cargada, usarla directamente
    if (navData.cessionData) {
      const data = navData.cessionData;
      if (!data.objContract) {
        setError('No se encontraron datos para esta cesion.');
        setLoading(false);
        return;
      }
      setCesion(mapData(data));
      setDocActual(0);
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const data = await getCessionInformation({
          group: navData.grupo || '',
          order: navData.orden || '',
          cessionID: navData.cessionID || '',
          sellerID: 0
        });
        if (!data || !data.objContract) {
          setError('No se encontraron datos para esta cesion.');
          return;
        }
        setCesion(mapData(data));
        setDocActual(0);
      } catch (err) {
        setError(err.message || 'No se pudo cargar la informacion de la cesion.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [location.state]);

  const documentos = cesion?.documentos || [];
  const docIndex = Math.min(docActual, Math.max(0, documentos.length - 1));
  const docUrl = buildDocUrl(documentos[docIndex]);

  const handleAnterior = useCallback(() => setDocActual(i => Math.max(0, i - 1)), []);
  const handleSiguiente = useCallback(() => setDocActual(i => Math.min(documentos.length - 1, i + 1)), [documentos.length]);

  const handleConfirmarAprobar = useCallback(async () => {
    setModalAprobar(false);
    setProcesando(true);
    try {
      const session = getUserSession();
      await setCessionStatus({
        idCession: cesion.cessionID,
        cessionStatus: 'APROBADO',
        statusNotes: 'Aprobado por usuario Honda',
        userID: session?.id || 0,
      });
      await Swal.fire({ title: 'Cesion Aprobada', text: 'La cesion fue aprobada correctamente.', icon: 'success', confirmButtonText: 'Aceptar', confirmButtonColor: '#22c55e' });
      onNavigate('home');
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Error al Aprobar', text: err.message || 'No se pudo aprobar la cesion.', confirmButtonColor: '#ef4444' });
      window.location.href = "/revision-cesion";
    } finally {
      setProcesando(false);
    }
  }, [cesion, onNavigate]);

  const handleConfirmarRechazar = useCallback(async () => {
    if (!motivoRechazo.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Motivo requerido', text: 'Debe ingresar el motivo del rechazo.', confirmButtonColor: '#ef4444' });
      return;
    }
    setModalRechazar(false);
    setProcesando(true);
    try {
      const session = getUserSession();
      await setCessionStatus({
        idCession: cesion.cessionID,
        cessionStatus: 'RECHAZADO',
        statusNotes: motivoRechazo.trim(),
        userID: session?.id || 0,
      });
      await Swal.fire({ title: 'Cesion Rechazada', text: 'La cesion fue rechazada.', icon: 'success', confirmButtonText: 'Aceptar', confirmButtonColor: '#ef4444' });
      setMotivoRechazo('');
      onNavigate('home');
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Error al Rechazar', text: err.message || 'No se pudo rechazar la cesion.', confirmButtonColor: '#ef4444' });
    } finally {
      setProcesando(false);
    }
  }, [cesion, motivoRechazo, onNavigate]);

  const isPersonaJuridica = cesion?.personType?.includes('2') || cesion?.comprador?.tipo?.includes('2');

  // ── Loading ──
  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
          <HondaLoader message="Cargando datos de la cesion..." variant="inline" size="large" subtitle="Obteniendo informacion completa del expediente" />
        </div>
      </MainLayout>
    );
  }

  // ── Error ──
  if (error || !cesion) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <span className="material-icons text-5xl text-gray-300 mb-3 block">error_outline</span>
            <p className="text-gray-400">{error || 'No se pudieron cargar los datos de la cesion'}</p>
            <button onClick={() => onNavigate('back')} className="mt-4 px-5 py-2 border-2 border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
              <span className="material-icons text-sm">arrow_back</span>Volver al listado
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-4 py-4">

        {/* Titulo */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Aprobacion de Cesion</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Revise la documentacion y apruebe o rechace la cesion</p>
        </div>

        {/* Layout: izquierda (docs) + derecha (info) */}
        <div className="flex flex-col lg:flex-row gap-4">

          {/* ── COLUMNA IZQUIERDA: Visor de documentos ── */}
          <div className="flex-1 bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
            <div className="h-2 bg-gray-800"></div>

            {/* Cabecera negra con navegacion */}
            <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div className="flex items-center gap-4">
                  <p className="text-xs text-white">
                    <span className="font-bold">Documento:</span> {documentos[docIndex]?.documentName || 'Sin documento'}
                  </p>
                  <p className="text-xs text-white">
                    <span className="font-bold">Anexo:</span> {documentos.length > 0 ? `${docIndex + 1} / ${documentos.length}` : '0 / 0'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleAnterior} disabled={docIndex === 0 || documentos.length === 0}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <span className="material-icons text-sm">chevron_left</span>Anterior
                  </button>
                  <button onClick={handleSiguiente} disabled={docIndex === documentos.length - 1 || documentos.length === 0}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Siguiente<span className="material-icons text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Visor */}
            <div className="flex-1 p-3 flex items-center justify-center bg-gray-100" style={{ minHeight: '550px' }}>
              {documentos.length === 0 ? (
                <div className="text-center">
                  <span className="material-icons text-5xl text-gray-400 mb-2 block">description</span>
                  <p className="text-sm text-gray-500">No hay documentos disponibles</p>
                  <p className="text-xs text-gray-400 mt-1">Este expediente no tiene documentos adjuntos</p>
                </div>
              ) : docUrl ? (
                <iframe src={docUrl} title={documentos[docIndex]?.documentName} className="w-full h-full rounded-lg border border-gray-300" style={{ minHeight: '520px' }} />
              ) : (
                <div className="text-center">
                  <span className="material-icons text-5xl text-gray-400 mb-2 block">pending</span>
                  <p className="text-sm text-gray-500">Documento pendiente de carga</p>
                  <p className="text-xs text-gray-400 mt-1">{documentos[docIndex]?.documentName}</p>
                </div>
              )}
            </div>
          </div>

          {/* ── COLUMNA DERECHA: Datos de la cesion ── */}
          <div className="w-full lg:w-[35%] bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
            <div className="h-2 bg-gray-800"></div>

            {/* Cabecera negra con ID y botones */}
            <div className="px-3 py-2 border-b border-gray-700 bg-gray-800 flex items-center justify-between">
              <p className="text-xs font-bold text-white truncate mr-2">Cesion ID: {cesion.cessionID || 'N/A'}</p>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setModalRechazar(true)} disabled={procesando}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Rechazar
                </button>
                <button onClick={() => setModalAprobar(true)} disabled={procesando}
                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md transition text-xs font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Aprobar
                </button>
              </div>
            </div>

            {/* Acordiones con info */}
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '550px' }}>

              {/* CONTRATO */}
              <AccordionSection title="DATOS DEL CONTRATO" icon="description">
                <Fila label="Cession ID" value={cesion.cessionID} />
                <Fila label="Grupo" value={cesion.grupo} />
                <Fila label="Orden" value={cesion.orden} />
                <Fila label="Tipo de Formulario" value={cesion.tipoFormulario} />
                <Fila label="Tipo de Persona" value={cesion.personType} />
                {cesion.estado && (
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-xs text-gray-500">Estado</span>
                    <span className="text-xs font-medium text-gray-900">{cesion.estado}</span>
                  </div>
                )}
              </AccordionSection>

              {/* COMPRADOR */}
              <AccordionSection title={isPersonaJuridica ? 'COMPRADORA (EMPRESA)' : 'COMPRADOR'} icon={isPersonaJuridica ? 'business' : 'person_add'}>
                <Fila label="Nombre / Razon Social" value={cesion.comprador.nombre} />
                <Fila label="DNI / CUIT" value={cesion.comprador.dni} />
                <Fila label="Telefono" value={cesion.comprador.telefono} />
                <Fila label="Email" value={cesion.comprador.email} />
                {cesion.comprador.tipo && <Fila label="Tipo de Persona" value={cesion.comprador.tipo} />}
              </AccordionSection>

              {/* VENDEDOR */}
              <AccordionSection title="VENDEDOR (CEDENTE)" icon="person_remove">
                <Fila label="Nombre / Razon Social" value={cesion.vendedor.nombre} />
                <Fila label="DNI / CUIT" value={cesion.vendedor.dni} />
                <Fila label="Telefono" value={cesion.vendedor.telefono} />
                <Fila label="Email" value={cesion.vendedor.email} />
                {cesion.vendedor.tipo && <Fila label="Tipo de Persona" value={cesion.vendedor.tipo} />}
              </AccordionSection>


            </div>
          </div>
        </div>
      </div>

      {/* ── MODAL APROBAR ── */}
      {modalAprobar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <h3 className="text-white font-bold text-lg">Confirmar Aprobacion</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-green-600 text-2xl">check_circle</span>
                </div>
                <div>
                  <p className="text-gray-800 font-semibold">Aprobar Cesion</p>
                  <p className="text-gray-600 text-sm">Cesion ID: {cesion.cessionID} — Grupo {cesion.grupo} / Orden {cesion.orden}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 bg-green-50 border border-green-200 rounded-lg p-3">
                Esta accion aprobara la cesion. Esta accion no se puede deshacer.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setModalAprobar(false)} className="px-4 py-2 border-2 border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancelar</button>
              <button onClick={handleConfirmarAprobar} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2">
                <span className="material-icons text-sm">check</span>Confirmar Aprobacion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL RECHAZAR ── */}
      {modalRechazar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
              <h3 className="text-white font-bold text-lg">Confirmar Rechazo</h3>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="material-icons text-red-600 text-2xl">cancel</span>
                </div>
                <div>
                  <p className="text-gray-800 font-semibold">Rechazar Cesion</p>
                  <p className="text-gray-600 text-sm">Cesion ID: {cesion.cessionID}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Motivo del Rechazo <span className="text-red-500">*</span></label>
                <textarea value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none"
                  rows={3} placeholder="Indique el motivo del rechazo..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => { setModalRechazar(false); setMotivoRechazo(''); }} className="px-4 py-2 border-2 border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">Cancelar</button>
              <button onClick={handleConfirmarRechazar} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors flex items-center gap-2">
                <span className="material-icons text-sm">close</span>Confirmar Rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AprobacionCesion;